import {FileTypeOptions} from '@Components/download-panel/components/file-type-option/file-type-option.types';
import type {DownloadColorProfileOptionType} from '@Components/download-panel/components/download-color-profile-option/download-color-profile-option.types';
import type {AJAXResponsePurchaseProduct} from '@Panels/poster-publishing-options-panel/components/publishing-embed-in-webpage-panel/publishing-embed-in-webpage-panel.types';
import type {LetterSizeOptionType} from '@Components/download-panel/components/download-adjust-letter-size-option/download-adjust-letter-size-option.types';
import type {PosterLoadObject} from '@PosterWhiteboard/poster/poster.types';
import {FillTypes} from '@PosterWhiteboard/classes/fill.class';
import {BackgroundTypeName} from '@PosterWhiteboard/page/background/background.class';
import {TransparencyStates} from '@PosterWhiteboard/page/background/image-background-item.class';
import {isPosterVideo} from '@Libraries/poster-object.library';

const WEB_DOWNLOAD_PRODUCT_ID = 63;
export const CHEAP_PRODUCT_ID = 85;
export const EXPENSIVE_PRODUCT_ID = 86;

export const DEFAULT_VIDEO_POSTER_GIF_DURATION = 10;
export const DEFAULT_IMAGE_POSTER_GIF_DURATION = 10;

export enum DownloadPublishMode {
  RAYDIANT = 'raydiant',
  SIGNAGELIVE = 'signagelive',
  SCREENCLOUD = 'screencloud',
  DIGITAL_SIGNAGE = 'digital-signage',
  EMBED_LINKS = 'embed-links',
  DOWNLOAD = 'download',
}

export enum DownloadDialogSource {
  EDITOR = 'editor',
  MYSTUFF = 'mystuff',
  DOWNLOAD_OPTIONS_PAGE = 'downloadOptionsPage',
}

export enum DownloadDurationThresholds {
  ORIGINAL = 'original',
  SHORTER = 'shorter',
  LONGER = 'longer',
}

export enum VideoDesignSizeOptionType {
  SD = 'SD',
  HD = 'HD',
  FULL_HD = 'FULL_HD',
  UHD_4K = 'UHD_4K',
  ORIGINAL = 'ORIGINAL',
}

interface FreebiePollingStatus {
  isAvailable: boolean;
  data: {
    status: string;
    downloadUrl: string;
  };
}

export interface PosterProductAPIData {
  isPosterMaker: boolean;
  isExpensiveVideo: boolean;
  idPosterHash: string;
  idGalleryTemplate?: string;
  idTemplate: string;
}

export interface DownloadProductsData {
  id: string;
  name: string;
  description: string;
  rawPrice: number;
  price: string;
  productPrice: number;
  daysFree: number;
  premiumContentAdded: boolean;
  retriesLeft: number;
  creditsLeft: number;
  productCreditPrice: number;
  extraInfo: any[];
  galleryTemplateHash: string | null;
  status: string;
  orderId: number;
  purchaseId: number;
  encryptedPurchaseId: string;
  isWebDownload: boolean;
  orderFileURLs: any[];
  downloadUrl: number;
  downloadFileUrl: number;
  isFree: boolean;
  hashedId: string;
  posterId: string;
  isVideo: boolean;
  remainingCredits: number;
  format: string;
  maxDimension: string;
  height: number;
  width: number;
  hasWatermark: boolean;
  hasClickableLinks: boolean;
  isLargeSize: boolean;
  posterRenderId: string;
  iconClassName: string;
  needsPurchase: boolean;
  posterName: string;
  mimeType: string;
  needsRegenerationDueToDeletion: boolean;
}

export interface Format {
  id: string;
  name: string;
  description: string;
  tag?: string | null;
}

export interface ProductsResponse {
  productsData: DownloadProductsData[];
  // defaultProductId: number;
  // availableFormats: Record<string, Format[]>;
}

export interface DownloadCallbackResponse {
  type: string;
  idOrder: string;
  isRealTime: boolean;
  downloadUrl?: string;
  printShopLink?: string;
}

export interface DownloadFreebieResponse {
  downloadUrl?: string;
  generationKey?: string;
}

export interface BaseDownloadAPIData {
  productId: number;
  isSilent: boolean;
  hashedId: string;
}

export interface CheckoutDownloadAPIData {
  hashedId: string;
  productId: number;
  promotion: string;
  mode: string;
  currencyCode: string;
  isInAppPurchase: boolean;
}

interface ImageDownloadOptions {
  bleedValue: number;
  bleedUnits: string;
  scale: number;
}

interface PNGDownloadOptons extends ImageDownloadOptions {
  isTransparent: boolean;
}

interface JPGDownloadOptions extends ImageDownloadOptions {}

interface PDFDownloadOptions extends Omit<ImageDownloadOptions, 'scale'> {
  hasMargin: boolean;
  colorProfile: DownloadColorProfileOptionType;
  letterSize?: LetterSizeOptionType;
  isWebQuality: boolean;
}

export interface DownloadJPGAPIData {
  type: FileTypeOptions.JPG;
  downloadOptions: JPGDownloadOptions;
}

export interface DownloadPNGAPIData {
  type: FileTypeOptions.PNG;
  downloadOptions: PNGDownloadOptons;
}

export interface BaseVideoDownloadOptions {
  speed: number;
  duration: number;
  scale: number;
}

export interface BasePDFAPIData {
  downloadOptions: PDFDownloadOptions;
}

export interface DownloadPDFAPIData extends BasePDFAPIData {
  type: FileTypeOptions.PDF;
}

export interface DownloadMP4APIData {
  type: FileTypeOptions.MP4;
  downloadOptions: BaseVideoDownloadOptions;
}

export interface DownloadGIFAPIData {
  type: FileTypeOptions.GIF;
  downloadOptions: BaseVideoDownloadOptions;
}

// FIGURE OUT WEBP THING AND WEBM THING
export interface DownloadWEBPAPIData {
  type: FileTypeOptions.WEBP;
  downloadOptions: any;
}

export interface DownloadWEBMAPIData {
  type: FileTypeOptions.WEBM;
  downloadOptions: any;
}

export type DownloadPosterAPIData =
  | DownloadJPGAPIData
  | DownloadPNGAPIData
  | DownloadWEBPAPIData
  | DownloadMP4APIData
  | DownloadGIFAPIData
  | DownloadWEBMAPIData
  | DownloadPDFAPIData;

interface RealTimeOrderProgressData {
  id: string;
  percentageDone: number;
  sleepTime: number;
}

export interface RealTimeOrderProgressResponse {
  percentageDone: number;
  heaviness: string;
  idProduct: number;
  downloadUrl?: string;
  renderId?: string;
  estimatedTimeInSeconds?: number;
  estimatedTimeStr: string;
  printShopLink?: string;
}
export const getOrderProgress = async (data: RealTimeOrderProgressData): Promise<RealTimeOrderProgressResponse> => {
  return (await window.PMW.readLocal('cart/getRealTimeOrderProgress', {
    id: data.id,
    percentageDone: data.percentageDone,
    sleepTime: data.sleepTime,
  })) as RealTimeOrderProgressResponse;
};

export const getOngoingDownloadIds = async (): Promise<string[]> => {
  return (await window.PMW.readLocal('cart/getOngoingOrdersForUser', {
    idPoster: window.posterEditor?.whiteboard?.id,
  })) as string[];
};

export const onDownloadPoster = async (data: BaseDownloadAPIData, options: DownloadPosterAPIData): Promise<DownloadCallbackResponse> => {
  return (await window.PMW.writeLocal('cart/premiumDownload', {
    productId: data.productId,
    hid: data.hashedId,
    is: data.isSilent,
    ...options,
  })) as DownloadCallbackResponse;
};

export const onDownloadFreebie = async (data: BaseDownloadAPIData, options: DownloadPosterAPIData): Promise<DownloadFreebieResponse> => {
  return (await window.PMW.writeLocal('cart/freebieDownload', {
    productId: data.productId,
    hid: data.hashedId,
    is: data.isSilent,
    ...options,
  })) as DownloadFreebieResponse;
};

export const getFreebieStatus = async (generationKey: string): Promise<FreebiePollingStatus> => {
  return (await window.PMW.readLocal('poster/getFreebieStatus', {
    generationKey: generationKey,
  })) as FreebiePollingStatus;
};

export const onDownloadPosterUnpurchased = async (data: CheckoutDownloadAPIData, options: DownloadPosterAPIData): Promise<AJAXResponsePurchaseProduct> => {
  return (await window.PMW.writeLocal('cart/checkoutPublish', {
    idposter: data.hashedId,
    idproduct: data.productId,
    promotion: data.promotion,
    mode: data.mode,
    currencyCode: data.currencyCode,
    isInAppPurchase: data.isInAppPurchase ? 1 : 0,
    ...options,
  })) as AJAXResponsePurchaseProduct;
};

export const getWebDownloadPosterTypeNames = async (): Promise<string[]> => {
  return (await window.PMW.readLocal('postertype/getPosterTypeFromProductId', {
    idProduct: WEB_DOWNLOAD_PRODUCT_ID,
  })) as string[];
};

export const getProductsForPoster = async (posterObj: PosterLoadObject): Promise<ProductsResponse> => {
  return (await window.PMW.writeLocal('poster/getProductDataForDownloadDialog', {
    idPosterHash: posterObj.hashedID,
    idGalleryTemplate: posterObj.idGalleryTemplate,
    idTemplate: posterObj.idTemplate,
  })) as ProductsResponse;
};

export const triggerBrowserDownload = (url: string, posterName: string, extension: string): void => {
  window.PMW.downloadPoster(url, posterName, extension);
};

export const getRecommendedFileTypeFromPosterObject = (posterObject: PosterLoadObject): FileTypeOptions => {
  const pageKeys = Object.keys(posterObject.pages.pagesHashMap);
  let isVideo = false;
  let isTransparent = false;
  for (const eachPage of pageKeys) {
    const page = posterObject.pages.pagesHashMap[eachPage];
    if (
      (page.background.details.type === BackgroundTypeName.COLOR && page.background.details.fill.fillType === FillTypes.NONE) ||
      (page.background.details.type === BackgroundTypeName.IMAGE && page.background.details.imageBackgroundItemObject.transparency === TransparencyStates.ON)
    ) {
      isTransparent = true;
      break;
    }
  }

  isVideo = isPosterVideo(posterObject);

  if (isVideo) {
    return FileTypeOptions.MP4;
  }

  if (isTransparent) {
    return FileTypeOptions.PNG;
  }

  return FileTypeOptions.JPG;
};

export const isDurationEnabledForGif = (posterObject: PosterLoadObject): boolean => {
  return isPosterVideo(posterObject);
};

export const getDurationForPoster = (poster: PosterLoadObject): number => {
  const pageKeys = Object.keys(poster.pages.pagesHashMap);
  if (isPosterVideo(poster)) {
    let duration = 0;
    for (const eachPage of pageKeys) {
      const page = poster.pages.pagesHashMap[eachPage];
      duration += page.duration;
    }
    return duration;
  } else {
    return 5 * pageKeys.length;
  }
};
